import { supabase } from '../supabaseClient';

// --- PRIVATE HELPERS ---

const generateRoomCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Rejects after `ms` milliseconds — used with Promise.race to guard Supabase
// calls that can hang indefinitely on transient network issues.
const withTimeout = (promise, ms, label = 'Supabase call', onTimeout = null) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => {
        if (onTimeout) onTimeout();
        reject(new Error(`${label} timed out after ${ms}ms`));
      }, ms)
    ),
  ]);

// --- ROOM CRUD ---

export const createRoom = async (host, numPlayers, mapAlgorithm = 'corridors', isPrivate = false, settings = {}) => {
  const code = generateRoomCode();

  const { data, error } = await supabase
    .from('rooms')
    .insert({
      code,
      host,
      status: 'waiting',
      num_players: numPlayers,
      players: [{ username: host, ready: true, isAI: false }],
      settings,
      map_algorithm: mapAlgorithm,
      is_private: isPrivate,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const createOrJoinRematch = async (sourceCode) => {
  const { data, error } = await supabase.rpc('create_or_join_rematch', {
    p_source_code: sourceCode,
    p_room_code: generateRoomCode(),
  });

  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
};

/**
 * Join a waiting room atomically via RPC.
 * join_room RPC confirmed deployed (functions.sql §8).
 * SELECT FOR UPDATE prevents concurrent-join race; idempotent.
 */
export const joinRoom = async (code, _username) => {
  const { data: rpcData, error } = await supabase.rpc('join_room', { p_code: code });
  if (error) throw error;
  const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
  return row;
};

// Rejoin existing game in progress (or return to waiting lobby)
export const rejoinGame = async (code, username) => {
  const { data, error } = await supabase.rpc('rejoin_game', {
    p_code: code,
    p_username: username,
  });

  if (error) throw new Error(error.message);
  return Array.isArray(data) ? data[0] : data;
};

/**
 * Apply a move via server-side RPC (SEC-AUTH-1).
 * Server validates turn, cell legality, marker index; computes new board,
 * score, next player; writes atomically under SELECT FOR UPDATE.
 *
 * @param {string} code  — room code
 * @param {Object} move  — { type:'place', markerIndex, row, col }
 *                       or { type:'swap', targetPlayerIndex }
 * @returns {Object} new game_state with extra `gameOver` boolean
 */
export const applyMove = async (code, move) => {
  const controller = new AbortController();
  const rpc = supabase
    .rpc('apply_move', { p_code: code, p_move: move })
    .abortSignal(controller.signal)
    .then(r => r);

  const { data, error } = await withTimeout(rpc, 8000, 'applyMove', () => controller.abort());
  if (error) throw error;
  return data;
};

// Finalize room at game end — writes status='finished' AND full ELO data atomically.
// BUG-SPECTATOR-ELO: single write means spectators get ratingChange+newRating in one Realtime event.
export const finalizeRoom = async (code, sortedPlayersWithElo) => {
  const { error } = await supabase.rpc('finalize_room', {
    p_code: code,
    p_final_players: sortedPlayersWithElo,
  });

  if (error) throw error;
};

/**
 * Atomically sets status='playing' AND game_state in a single transaction.
 * start_game RPC confirmed deployed (functions.sql §10).
 * Host-only enforcement done server-side.
 */
export const startGame = async (code, gameState) => {
  const { error } = await supabase.rpc('start_game', {
    p_code: code,
    p_game_state: gameState,
  });
  if (error) throw error;
};

/**
 * Toggle ready state atomically via RPC.
 * toggle_player_ready RPC confirmed deployed (functions.sql §3).
 */
export const togglePlayerReady = async (code, username) => {
  const { error } = await supabase.rpc('toggle_player_ready', {
    p_code: code,
    p_username: username,
  });
  if (error) throw error;
};

/**
 * Add a real DB bot to a room atomically via RPC.
 * add_bot_to_room RPC confirmed deployed (functions.sql §4).
 * `botProfile` from getAvailableBots() — { username, rating, bot_persona }.
 */
export const addBotToRoom = async (code, botProfile) => {
  const botEntry = {
    username: botProfile.username,
    ready: true,
    isAI: true,
    isBot: true,
    bot_persona: botProfile.bot_persona || 'v1',
    rating: botProfile.rating || 1500,
  };
  const { error } = await supabase.rpc('add_bot_to_room', { p_code: code, p_bot: botEntry });
  if (error) throw error;
};

/**
 * Leave a room atomically via RPC.
 * leave_room RPC confirmed deployed (functions.sql §9).
 * Handles host reassignment and cascade-delete server-side.
 */
export const leaveRoom = async (code, _username) => {
  const { error } = await supabase.rpc('leave_room', { p_code: code });
  if (error) throw error;
};

export const checkRoomDisconnects = async (code) => {
  const { data, error } = await supabase.rpc('check_room_disconnects', { p_room_code: code });
  if (error) throw error;
  return data || 0;
};

// Convert leaving player to a bot seat atomically on the server.
export const convertPlayerToRealBot = async (code, username, targetRating = 1500) => {
  const { error } = await supabase.rpc('replace_player_with_bot', {
    p_code: code,
    p_username: username,
    p_target_rating: targetRating,
  });

  if (error) throw error;
};

// Subscribe to room changes (returns unsubscribe function)
export const subscribeToRoom = (code, callback) => {
  const channel = supabase
    .channel(`room:${code}`)
    .on('postgres_changes',
      { event: '*', schema: 'public', table: 'rooms', filter: `code=eq.${code}` },
      (payload) => {
        if (payload.eventType === 'DELETE' || !payload.new || !payload.new.code) return;
        callback(payload.new);
      }
    )
    .subscribe();

  return () => channel.unsubscribe();
};

// Subscribe to room list changes (Realtime instead of polling)
export const subscribeToRoomsList = (callback) => {
  const channel = supabase
    .channel('lobby-rooms')
    .on('postgres_changes',
      { event: '*', schema: 'public', table: 'rooms' },
      () => callback()
    )
    .subscribe();

  return () => channel.unsubscribe();
};

/**
 * Find active games/lobbies the player can rejoin.
 * find_rejoinable_games RPC confirmed deployed (functions.sql §5).
 * Waiting rooms are always fetched directly (RPC only covers playing games).
 */
export const findRejoinableGames = async (username) => {
  const { data: waitingData } = await supabase
    .from('rooms')
    .select('code, host, num_players, players, settings, game_state, status, map_algorithm, is_private, updated_at')
    .eq('status', 'waiting');

  const waitingRooms = (waitingData || []).filter(room =>
    (room.players || []).some(p => p.username === username && !p.isAI)
  );

  const { data: rpcData, error } = await supabase.rpc('find_rejoinable_games', { p_username: username });
  if (error) throw error;

  return [...(rpcData || []), ...waitingRooms];
};

// Kick a player out of a waiting room (host-only, enforced server-side via RPC)
// kick_player_from_room RPC confirmed deployed (functions.sql §7).
export const kickPlayerFromRoom = async (code, usernameToKick) => {
  const { error } = await supabase.rpc('kick_player_from_room', {
    p_code: code,
    p_target: usernameToKick,
  });
  if (error) throw error;
};

// Manual/admin fallback only.
// Automatic cleanup is handled by the pg_cron job in database/functions.sql.
// Do NOT call this automatically from the client.
export const cleanupBotOnlyRooms = async () => {
  const { data, error } = await supabase
    .from('rooms')
    .select('code, players')
    .eq('status', 'waiting');

  if (error || !data) return;

  const orphaned = data.filter(room => {
    const players = room.players || [];
    return players.length === 0 || players.every(p => p.isAI);
  });

  if (orphaned.length === 0) return;

  await Promise.all(
    orphaned.map(room => supabase.from('rooms').delete().eq('code', room.code))
  );
};

// Get available rooms that are waiting and not full
export const getAvailableRooms = async () => {
  const { data, error } = await supabase
    .from('rooms')
    .select('code, host, num_players, players, settings, map_algorithm, is_private, status, created_at')
    .eq('status', 'waiting')
    .eq('is_private', false)
    .order('created_at', { ascending: false })
    .limit(10);

  if (error) throw error;

  return data.filter(room => {
    const players = room.players || [];
    return players.length < room.num_players && players.some(p => !p.isAI);
  });
};

// Get rooms with active games that can be spectated
export const getWatchableRooms = async () => {
  const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('rooms')
    .select('code, host, num_players, players, settings, map_algorithm, status, updated_at')
    .eq('status', 'playing')
    .eq('is_private', false)
    .eq('is_bot_fight', false)
    .gte('updated_at', fiveMinAgo)
    .order('updated_at', { ascending: false })
    .limit(10);

  if (error) throw error;
  return data || [];
};

/**
 * Create a bot-fight room atomically via RPC.
 * The client pre-builds game_state (board + bot players); the RPC assigns a
 * room code, marks is_bot_fight=TRUE, sets status='playing', and returns the row.
 * The matching RPC lives in database/functions.sql.
 */
export const createBotFightRoom = async (gameState, mapAlgorithm = 'corridors', settings = {}) => {
  const { data, error } = await supabase
    .rpc('create_bot_fight_room', {
      p_game_state:    gameState,
      p_map_algorithm: mapAlgorithm,
      p_settings:      settings,
    })
    .single();
  if (error) throw error;
  return data;
};
